"use client";

import {
  isRouteErrorResponse,
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
} from "react-router";

import type { Route } from "./+types/root";

import "./app.css";
import "./i18n";

import { useEffect, useMemo, useState } from "react";
import { loadConfig } from "lib/config";
import useUserState from "./stores/user";
import useSettingState from "./stores/setting";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "lib/query-client";
import { navRoutes } from "./routes/nav-routes";
import MainLayout from "./components/layouts/main-layout";
import { Toaster } from "./components/ui/sonner";
import useUiThemeState from "./stores/ui/theme";
import { useResolvedMode } from "./hooks/resolved-mode-hook";
import { initMsal } from "./utils/msal";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/styles";
import type { PublicClientApplication } from "@azure/msal-browser";
import { LoadingScreen } from "./components/common/loading-screen";
import { ThemeProvider } from "./components/ui/utils/theme-provider";
import type { ScRoute, UserInfo } from "./types/ui";
import useLicenseState from "./stores/license";
import { LicenseApi } from "./api/license-api";
import { SettingsApi } from "./api/settings-api";
import { CompanyLogo } from "./components/common/company-logo";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export let msalInstance: PublicClientApplication | undefined;

export function HydrateFallback() {
  return <LoadingScreen />;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const themeState = useUiThemeState();
  const mode = useResolvedMode();
  return (
    <html
      lang="en"
      className={mode}
      data-theme={`${themeState.theme}-${mode}`}
    >
      <head>
        <title>SensorOnline</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <ThemeProvider defaultMode="dark">
          <QueryClientProvider client={queryClient}>
            {children}
            <Toaster position="top-center" />
          </QueryClientProvider>
        </ThemeProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  useEffect(() => {
    initMsal().then((d) => (msalInstance = d));
  }, []);

  const userState = useUserState();
  const settingState = useSettingState();
  const licenseState = useLicenseState();
  const [layoutUser, setLayoutUser] = useState<UserInfo | null>(null);
  const logo = <CompanyLogo className="h-8 md:h-12 md:w-full object-contain" />;

  useEffect(() => {
    document.title = settingState.getDocumentTitle();
  }, [
    settingState.companySettings?.COMPANY_BRANDING_TITLE,
    settingState.globalSettings?.branding,
  ]);

  const routes: ScRoute[] = useMemo(() => navRoutes, []);
  queryClient.setDefaultOptions({
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 1000 * 60 * 1, // 5 minutes
    },
  });

  useEffect(() => {
    if (userState.user) {
      setLayoutUser({
        userId: userState.user.userId ?? 0,
        fullName: userState.user.fullName ?? "N/A",
        email: userState.user.email,
        companyName: userState.company?.name ?? "",
        companyId: userState.company?.companyId ?? 1,
      });
      return;
    }
    setLayoutUser(null);
  }, [userState.user, userState.company]);

  const [ready, setReady] = useState(false);
  useEffect(() => {
    loadConfig().then(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return;

    new LicenseApi().getTerms().then((res) => {
      if (res.ok && res.data) licenseState.setLicenseTerms(res.data);
    });

    // Force a real fetch rather than trusting whatever's cached
    queryClient.fetchQuery({
      queryKey: ["global:settings"],
      queryFn: async () => {
        const res = await new SettingsApi().getGlobal();
        if (res.ok && res.data) {
          settingState.setGlobalSettings(res.data);
          return res.data;
        }
        return undefined;
      },
      staleTime: 0,
    });
  }, [ready]);

  if (!ready) return <LoadingScreen />;

  return <MainLayout routes={routes} user={layoutUser} logo={logo} />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
